import { useState } from 'react';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import * as Yup from 'yup';
import { Turnstile } from '@marsidev/react-turnstile';
import { Box, styled, useTheme } from '@mui/material';

import { Button, TextField, Typography } from '@zendesk/forethought-elements';
import LandingPageLayout from 'src/components/reusable-components/landing-page-layout';
import Spinner from 'src/components/spinner';
import { Routes } from 'src/utils/enums';
import {
  buildZendeskHelpCenterSignInUrl,
  getDashboardRedirect,
  mergeURLParams,
} from 'src/utils/helpCenterAuthUtils';
import {
  getStoredZendeskSubdomain,
  setStoredZendeskSubdomain,
} from 'src/utils/zendeskSubdomainStorage';

interface FormState {
  email: string;
  subdomain: string;
  token: string;
}

export default function OrgDiscoveryLogin() {
  const { t } = useTranslation('authentication-wall');

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email(t('orgDiscoveryLogin.invalidEmail'))
      .required(t('common.fieldRequired')),
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { palette } = useTheme();
  // ?zd=true is a shortcut: surface the subdomain field upfront. Without
  // it we start email-only and reveal subdomain only after a 404, on the
  // theory that an unknown email may belong to a ZD-provisioned tenant
  // we'll match via subdomain on recall. Same pattern in ValidateEmailForm.
  const isZendeskMode = searchParams.get('zd') === 'true';
  const [needsSubdomain, setNeedsSubdomain] = useState(false);
  const subdomainVisible = isZendeskMode || needsSubdomain;
  // Persisted across the isLoading toggle (Formik unmounts during the
  // spinner, so we re-seed initialValues from these on the next render
  // and the user doesn't have to retype on a 404).
  const [persistedEmail, setPersistedEmail] = useState('');
  const [persistedSubdomain, setPersistedSubdomain] = useState(
    searchParams.get('subdomain') ?? getStoredZendeskSubdomain(),
  );

  const handleSubmit = async ({ email, subdomain, token }: FormState) => {
    setError('');
    setPersistedEmail(email);
    setPersistedSubdomain(subdomain);
    setIsLoading(true);
    const trimmedSubdomain = subdomain.trim();

    if (trimmedSubdomain) {
      setStoredZendeskSubdomain(trimmedSubdomain);
    }

    try {
      const redirectUrl = await getDashboardRedirect({
        email,
        ...(trimmedSubdomain ? { subdomain: trimmedSubdomain } : {}),
        turnstile_token: token,
      });

      // When the response points at a ZD-provisioned tenant, hop through
      // the tenant's sign-in page → /hc-redirect → helpcenter. Otherwise
      // continue with the legacy dashboard-login flow.
      const zendeskSignInUrl = buildZendeskHelpCenterSignInUrl(redirectUrl);
      if (zendeskSignInUrl) {
        window.location.href = zendeskSignInUrl;
        return;
      }

      window.location.href = mergeURLParams(redirectUrl, email);
    } catch (err) {
      setIsLoading(false);
      if (err instanceof Response && err.status === 404) {
        if (trimmedSubdomain) {
          navigate(Routes.ORG_NOT_FOUND);
          return;
        }
        setNeedsSubdomain(true);
      } else {
        setError(t('common.somethingWentWrong'));
      }
    }
  };

  const initialValues: FormState = {
    email: persistedEmail,
    subdomain: persistedSubdomain,
    token: '',
  };

  return (
    <LandingPageLayout>
      {isLoading ? (
        <Spinner />
      ) : (
        <Container>
          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
          >
            {({
              errors,
              handleChange,
              handleSubmit,
              setFieldValue,
              touched,
              values,
            }) => (
              <Form onSubmit={handleSubmit}>
                <Box
                  sx={{
                    mb: '16px',
                  }}
                >
                  <Typography variant='font32'>
                    {t('common.signInToForethought')}
                  </Typography>
                </Box>
                <TextField
                  aria-label={t('common.email')}
                  error={touched.email ? errors.email : undefined}
                  name='email'
                  onChange={(...args) => {
                    handleChange(...args);
                  }}
                  placeholder={t('common.enterYourEmail')}
                  type='text'
                  value={values.email}
                />
                {subdomainVisible && (
                  <TextField
                    aria-label={t('common.subdomain')}
                    name='subdomain'
                    onChange={handleChange}
                    placeholder={
                      needsSubdomain
                        ? t('common.zendeskSubdomain')
                        : t('common.subdomainOptional')
                    }
                    type='text'
                    value={values.subdomain}
                  />
                )}
                <TurnstileContainer>
                  <Turnstile
                    onError={() => setError(t('common.couldNotValidate'))}
                    onSuccess={token => setFieldValue('token', token)}
                    options={{ theme: 'light' }}
                    siteKey={TURNSTILE_SITEKEY}
                  />
                </TurnstileContainer>
                {error && (
                  <Box>
                    <Typography
                      color={palette.colors.red[500]}
                      variant='font16'
                    >
                      {error}
                    </Typography>
                  </Box>
                )}
                <Button
                  disabled={needsSubdomain && !values.subdomain.trim()}
                  fullWidth
                  isLoading={isLoading}
                  size='large'
                  type='submit'
                  variant='main'
                >
                  {t('orgDiscoveryLogin.next')}
                </Button>
              </Form>
            )}
          </Formik>
        </Container>
      )}
    </LandingPageLayout>
  );
}

const Container = styled('div')`
  display: flex;
  flex-direction: column;
`;

const Form = styled('form')`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const TurnstileContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
`;
